import type { ReactNode } from 'react';
import { codigoDesgaste, dividirNome, partesPreco } from '../lib/format';

interface Props {
  /** Preço já formatado na moeda de cada lado — ver partesPreco(). */
  preco: string;
  nome: string;
  imagem: string | null;
  /** Sigla ou nome do desgaste; o card resolve os dois. */
  desgaste?: string | null;
  statTrak?: boolean;
  float?: number | null;
  adesivos?: { imageUrl: string | null; name: string | null }[];
  selecionado?: boolean;
  bloqueado?: boolean;
  /** Por que o item não pode ser escolhido (item travado, fora do catálogo…). */
  motivo?: string | null;
  /** Abre a peça no CS2. Fica fora do botão: link dentro de botão é inválido. */
  inspectUrl?: string | null;
  /** Linha solta no pé do card (contagem de desgastes, valor de mercado…). */
  rodape?: ReactNode;
  onClick?: () => void;
  title?: string;
}

/**
 * O card de item das duas pontas da troca.
 *
 * O mesmo desenho dos dois lados é o que deixa a comparação imediata: o que
 * sai do inventário e o que vem do catálogo são a mesma coisa para quem troca,
 * e desenhá-los diferente faria o cliente reler os dois lados a cada olhada.
 *
 * A leitura é de cima para baixo pela ordem em que a decisão acontece: preço,
 * estado da peça, a arte e só então o nome.
 */
export function UpgradeCard({
  preco,
  nome,
  imagem,
  desgaste,
  statTrak,
  float,
  adesivos,
  selecionado = false,
  bloqueado = false,
  motivo,
  inspectUrl,
  rodape,
  onClick,
  title,
}: Props) {
  const [inteiro, centavos] = partesPreco(preco);
  const { arma, skin } = dividirNome(nome);
  const sigla = codigoDesgaste(desgaste, nome);
  const temFloat = float !== null && float !== undefined && Number.isFinite(float);

  return (
    <div className={`up-card ${selecionado ? 'on' : ''} ${bloqueado ? 'off' : ''}`}>
      <button
        type="button"
        className="up-card-face"
        onClick={onClick}
        disabled={bloqueado || !onClick}
        title={title ?? motivo ?? nome}
      >
        <div className="up-card-topo">
          <span className="up-preco">
            {inteiro}
            <small>{centavos}</small>
          </span>
          <span className="up-selos">
            {statTrak && <i className="up-selo st">ST</i>}
            {sigla && <i className={`up-selo w-${sigla}`}>{sigla}</i>}
          </span>
        </div>

        {/* A barra só existe quando há peça de verdade: no nível da skin o
            float ainda não foi escolhido, e uma barra vazia sugeriria que a
            peça é 0.0000. */}
        {temFloat && (
          <div className="up-wear">
            <div className="up-wear-trilha">
              <i className="up-wear-marca" style={{ left: `${Math.min(100, Math.max(0, float! * 100))}%` }} />
            </div>
            <span className="up-wear-valor">{float!.toFixed(4)}</span>
          </div>
        )}

        <div className="up-arte">
          {imagem ? <img src={imagem} alt="" loading="lazy" /> : <span className="sem-img" />}
        </div>

        {adesivos && adesivos.length > 0 && (
          <div className="up-adesivos">
            {adesivos.slice(0, 5).map((a, i) =>
              a.imageUrl ? (
                <img key={i} src={a.imageUrl} alt="" title={a.name ?? ''} loading="lazy" />
              ) : (
                <span key={i} className="up-adesivo-vazio" title={a.name ?? ''} />
              ),
            )}
          </div>
        )}

        <div className="up-card-nome">
          {arma && <span className="up-arma">{arma}</span>}
          <span className="up-skin">{skin}</span>
        </div>

        {bloqueado && motivo && <span className="up-motivo">{motivo}</span>}
        {rodape && <span className="up-rodape">{rodape}</span>}
        {selecionado && <span className="up-check">✓</span>}
      </button>

      {inspectUrl && (
        <a className="up-lupa" href={inspectUrl} title="Inspecionar esta peça no CS2">
          🔍
        </a>
      )}
    </div>
  );
}
